import React, { useEffect, useRef, useState } from "react";
import LeftIcon from "../../images/LeftIcon";
import ArrowRightIcon from "../../images/ArrowRightIcon";
import DraftIcon from "../../images/DraftIcon";
import SaveIcon from "../../images/SaveIcon";
import {
  webLoadingFalse,
  webLoadingTrue,
  webNavigate,
} from "../../services/navigate.service";
import {
  createGroupTax,
  createTax,
  getDetailTax,
  getGroupTax,
  updateGroupTax,
  updateTax,
} from "../../services/tax.services";
import { validateNoSpecialChars } from "../../validation/ValidationInput";
import DatePicker from "react-datepicker";
import { getCountries } from "../../services/country.service";
import PopupLocation from "../../components/PopupLocation";
import { showToast } from "../../services/toast.service";
import { showApiError } from "../../services/apiError";
import { coreFormLimits, limitProps, useFieldLimits, validateAgainstLimits } from "../../services/fieldLimits";
import FieldCounter from "../../components/FieldCounter";
import BackIcon from "../../images/BackIcon";
import { DropdownSearch } from "../../components/DropdownSearch";
import { useResponsive } from "../../hook/useResponsive";
import { useFormKeyboard } from "../../hook/useFormKeyboard";

interface Country {
  id: string;
  code: string;
  name: string;
}

/** Local calendar day start — avoids UTC shift when serializing to ISO. */
const startOfLocalDay = (d: Date): Date => {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
};

/** Local calendar day end — matches user-selected “valid to” date on the server. */
const endOfLocalDay = (d: Date): Date => {
  const out = new Date(d);
  out.setHours(23, 59, 59, 999);
  return out;
};

const CreateTax: React.FC = () => {
  const preLanguage = localStorage.getItem("language");
  const [formValues, setFormValues] = useState<any>({
    taxName: "",
    taxCode: "",
    primaryRate: "",
    countryId: "",
    taxGroupId: "",
    ksFiscalCode: "",
  });
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [language, setLanguage] = useState<any>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const { isMobile, getWidth } = useResponsive();
  const [errors, setErrors] = useState<any>({});
  const limits = useFieldLimits(coreFormLimits("tax"));
  const [searchCountry, setSearchCountry] = useState("");
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const dropdownCountryRef = useRef<HTMLDivElement>(null);
  const [taxGroup, setTaxGroup] = useState<any>([]);
  const [searchGroupTax, setSearchGroupTax] = useState("");
  const [isGroupTaxDropdownOpen, setIsGroupTaxDropdownOpen] = useState(false);
  const dropdownGroupTaxRef = useRef<HTMLDivElement>(null);
  const path = window.location.pathname;
  const id: string = path.split("/").pop() || "";
  // No autoFocus: the first field is a date picker, which would open its calendar on load.
  const kb = useFormKeyboard();
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownCountryRef.current &&
        !dropdownCountryRef.current.contains(event.target as Node)
      ) {
        setIsCountryDropdownOpen(false);
      }
      if (
        dropdownGroupTaxRef.current &&
        !dropdownGroupTaxRef.current.contains(event.target as Node)
      ) {
        setIsGroupTaxDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  useEffect(() => {
    if (id !== "new") {
      fetchData(id);
    }
    const handleNewToken = () => {
      if (id !== "new") {
        fetchData(id);
      }
    };
    document.addEventListener("newToken", handleNewToken);
    return () => {
      document.removeEventListener("newToken", handleNewToken);
    };
  }, [path]);
  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);
  const fetchData = async (id: string) => {
    webLoadingTrue();
    try {
      const response: any = await getDetailTax(id);
      console.log("🚀 ~ fetchData ~ response:", response);
      setFormValues({
        ...response.data,
        ksFiscalCode:
          response.data.ksFiscalCode != null
            ? String(response.data.ksFiscalCode)
            : "",
      });
      setSearchCountry(response.data.country.countryName);
      setStartDate(new Date(response.data.validFrom));
      setEndDate(new Date(response.data.validTo));
      setSearchGroupTax(response.data.taxGroup.groupName);
    } catch (error) {
      console.error("Error loading terms:", error);
    } finally {
      webLoadingFalse();
    }
  };
  useEffect(() => {
    const fetchCountries = async () => {
      const data = await getCountries();
      const dataTerm = await getGroupTax({
        page: 1,
        limit: 50,
        searchValue: "",
        sort: "DESC",
      });
      setCountries(data);
      setTaxGroup(dataTerm.data);
    };
    fetchCountries();
    const handleNewToken = async () => {
      const data = await getCountries();
      const dataTerm = await getGroupTax({
        page: 1,
        limit: 50,
        searchValue: "",
        sort: "DESC",
      });
      setCountries(data);
      setTaxGroup(dataTerm.data);
    };
    document.addEventListener("newToken", handleNewToken);
    return () => {
      document.removeEventListener("newToken", handleNewToken);
    };
  }, []);
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    if (name === "ksFiscalCode") {
      if (value === "" || /^\d+$/.test(value)) {
        setFormValues((prevValues: any) => ({
          ...prevValues,
          [name]: value,
        }));
      }
      return;
    }
    setFormValues((prevValues: any) => ({
      ...prevValues,
      [name]: value,
    }));
  };
  const handleStartDate = (value: any) => {
    setStartDate(value);
    setFormValues({ ...formValues, validFrom: value });
  };
  const handleEndDate = (value: any) => {
    setEndDate(value);
    setFormValues({ ...formValues, validTo: value });
  };
  const handleCountrySelect = (country: any) => {
    setFormValues((prev: any) => ({ ...prev, countryId: country.id }));
    setSearchCountry(country.name);
    setIsCountryDropdownOpen(false);
  };
  const handleTaxGroupSelect = (tax: any) => {
    setFormValues((prev: any) => ({ ...prev, taxGroupId: tax.id }));
    setSearchGroupTax(tax.groupName);
    setIsGroupTaxDropdownOpen(false);
  };
  const handleClosePopup = () => {
    setShowPopup(false);
  };
  const handleOpenPopup = () => {
    setShowPopup(true);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: any = validateAgainstLimits(formValues, limits, language);
    if (!formValues.taxName) {
      newErrors.taxName = "TAX Name is required";
    }
    if (!formValues.taxCode) {
      newErrors.taxCode = "TAX Code is required";
    }
    if (!formValues.primaryRate) {
      newErrors.primaryRate = "Primary Rate is required";
    }
    if (!formValues.countryId) {
      newErrors.countryId = "Country is required";
    }
    if (!formValues.taxGroupId) {
      newErrors.taxGroupId = "Group Tax is required";
    }
    if (formValues.ksFiscalCode !== "" && formValues.ksFiscalCode != null) {
      const num = parseInt(formValues.ksFiscalCode, 10);
      if (Number.isNaN(num) || num < 0 || !Number.isInteger(num)) {
        newErrors.ksFiscalCode = "Must be a whole number (0 or positive integer)";
      }
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }
    webLoadingTrue();
    const ksFiscalCode =
      formValues.ksFiscalCode === "" || formValues.ksFiscalCode == null
        ? undefined
        : parseInt(formValues.ksFiscalCode, 10);
    const { ksFiscalCode: _omit, ...formRest } = formValues;
    const payload: any = {
      ...formRest,
      primaryRate: Number(formValues.primaryRate),
      validFrom: startOfLocalDay(startDate),
      validTo: endOfLocalDay(endDate),
    };
    if (ksFiscalCode !== undefined && !Number.isNaN(ksFiscalCode)) {
      payload.ksFiscalCode = ksFiscalCode;
    }
    if (id === "new") {
      try {
        const response: any = await createTax(payload);
        if (response?.data?.id) {
          webNavigate("/" + preLanguage + "/tax");
          webLoadingFalse();
          showToast({
            message: "Tax Created !",
            type: "success",
            duration: 20000,
          });
        }
      } catch (error: any) {
        webLoadingFalse();

        showApiError(error, { language, setFieldErrors: setErrors });
      }
    } else {
      try {
        const response: any = await updateTax(id, payload);
        if (response?.data?.id) {
          webNavigate("/" + preLanguage + "/tax");
          webLoadingFalse();
          showToast({
            message: "Tax Updated !",
            type: "success",
            duration: 20000,
          });
        }
      } catch (error: any) {
        webLoadingFalse();

        showApiError(error, { language, setFieldErrors: setErrors });
      }
    }
    // try {
    //     const payload = { ...formValues, primaryRate: Number(formValues.primaryRate), validFrom: formValues?.validFrom ? formValues?.validFrom : new Date(), validTo: formValues?.validTo ? formValues?.validTo : new Date(), }
    //     if (id === "new") {
    //         webLoadingTrue();
    //         await createTax(payload);
    //     } else {
    //         await updateTax(id, payload);
    //     }
    //     webNavigate("/"+preLanguage+"/tax");
    // } catch (error) {
    //     console.error("Error saving term:", error);
    // } finally {
    //     webNavigate("/"+preLanguage+"/tax");
    // }
  };

  return (
    <div {...kb.scopeProps}>
      <div className="bg-white">
        <div className="flex items-center ">
          <button
            type="button"
            aria-label={language?.["Back"] ?? "Back"}
            className="w-6 h-6 flex items-center justify-center cursor-pointer"
            onClick={() => {
              webNavigate("/" + preLanguage + "/tax");
            }}
          >
            <BackIcon />
          </button>
          <p
            style={{
              fontSize: "14px",
              fontWeight: "500",
              lineHeight: "28px",
              color: "#222525",
            }}
            className="font-inter"
          >
            {language && language?.["Back"] ? language?.["Back"] : "Back"}
          </p>
        </div>
      </div>
      {id === "new" ? (
        <p
          style={{
            margin: "20px 0px",
            fontSize: "20px",
            fontWeight: "500",
            lineHeight: "28px",
            color: "#222525",
          }}
          className="font-inter"
        >
          {language && language?.[`Add TAX`]
            ? language?.[`Add TAX`]
            : `Add TAX`}
        </p>
      ) : (
        <p
          style={{
            margin: "20px 0px",
            fontSize: "20px",
            fontWeight: "500",
            lineHeight: "28px",
            color: "#222525",
          }}
          className="font-inter"
        >
          {language && language?.[`Edit TAX`]
            ? language?.[`Edit TAX`]
            : `Edit TAX`}
        </p>
      )}
      <form onSubmit={handleSubmit} style={{ height: isMobile ? "auto" : "calc(100% - 163px)" }}>
        <div
          className=" bg-white flex items-start justify-between"
          style={{ flexWrap: "wrap", gap: "24px" }}
        >
          <div className="space-y-2" style={{ width: isMobile ? "100%" : "calc(50% - 12px)" }}>
            <div className="flex items-center justify-between">
              <p
                className="text-[14px] font-inter leading-[20px] font-medium"
                style={{ color: "#4A5353" }}
              >
                {language && language?.["Valid From"]
                  ? language?.["Valid From"]
                  : "Valid From"}
              </p>
            </div>
            <div className="w-full">
              <DatePicker
                showTimeSelect
                dateFormat="dd-MM-yyyy"
                placeholderText={
                  language && language?.["Enter a start date"]
                    ? language?.["Enter a start date"]
                    : "Enter a start date"
                }
                name="startTime"
                className={`text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                selected={startDate}
                onChange={(e) => handleStartDate(e)}
              />
            </div>
          </div>
          <div className="space-y-2" style={{ width: isMobile ? "100%" : "calc(50% - 12px)" }}>
            <div className="flex items-center justify-between">
              <p
                className="text-[14px] font-inter leading-[20px] font-medium"
                style={{ color: "#4A5353" }}
              >
                {language && language?.["Valid To"]
                  ? language?.["Valid To"]
                  : "Valid To"}
              </p>
            </div>
            <div className="w-full">
              <DatePicker
                showTimeSelect
                dateFormat="dd-MM-yyyy"
                placeholderText={
                  language && language?.["Enter a end date"]
                    ? language?.["Enter a end date"]
                    : "Enter a end date"
                }
                name="startTime"
                className={`text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                selected={endDate}
                onChange={(e) => handleEndDate(e)}
              />
            </div>
          </div>
          <div
            className="space-y-2"
            style={{ width: isMobile ? "100%" : "calc(50% - 12px)" }}
          >
            <div className="flex items-center">
              <p
                className="text-[14px] font-inter leading-[20px] font-medium"
                style={{ color: "#4A5353" }}
              >
                {language && language?.["TAX Name"]
                  ? language?.["TAX Name"]
                  : "TAX Name"}
              </p>
              <p
                className="text-[14px] font-inter leading-[20px]"
                style={{ color: "#BA0E13" }}
              >
                {language && language?.["*"] ? language?.["*"] : "*"}
              </p>
            </div>
            <div className="w-full">
              <input
                required
                type="text"
                name="taxName"
                {...limitProps(limits, "taxName")}
                value={formValues.taxName}
                onChange={handleChange}
                className="text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-lg outline-none focus-none bg-transparent w-full"
              />
              <FieldCounter value={formValues.taxName} max={limits.taxName?.maxLength} />
            </div>
            {errors.taxName && <p className="error">{errors.taxName}</p>}
          </div>
          <div
            className="space-y-2"
            style={{ width: isMobile ? "100%" : "calc(50% - 12px)" }}
          >
            <div className="flex items-center">
              <p
                className="text-[14px] font-inter leading-[20px] font-medium"
                style={{ color: "#4A5353" }}
              >
                {language && language?.["TAX Code"]
                  ? language?.["TAX Code"]
                  : "TAX Code"}
              </p>
              <p
                className="text-[14px] font-inter leading-[20px]"
                style={{ color: "#BA0E13" }}
              >
                {language && language?.["*"] ? language?.["*"] : "*"}
              </p>
            </div>
            <div className="w-full">
              <input
                required
                type="text"
                name="taxCode"
                {...limitProps(limits, "taxCode")}
                value={formValues.taxCode}
                onChange={handleChange}
                className="text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-lg outline-none focus-none bg-transparent w-full"
              />
              <FieldCounter value={formValues.taxCode} max={limits.taxCode?.maxLength} />
            </div>
            {errors.taxCode && <p className="error">{errors.taxCode}</p>}
          </div>
          <div
            className="space-y-2"
            style={{ width: isMobile ? "100%" : "calc(33% - 12px)" }}
          >
            <div className="flex items-center">
              <p
                className="text-[14px] font-inter leading-[20px] font-medium"
                style={{ color: "#4A5353" }}
              >
                {language && language?.["Primary Rate"]
                  ? language?.["Primary Rate"]
                  : "Primary Rate"}
              </p>
              <p
                className="text-[14px] font-inter leading-[20px]"
                style={{ color: "#BA0E13" }}
              >
                {language && language?.["*"] ? language?.["*"] : "*"}
              </p>
            </div>
            <div className="w-full">
              <input
                required
                type="text"
                name="primaryRate"
                {...limitProps(limits, "primaryRate", "number")}
                value={formValues.primaryRate}
                onChange={handleChange}
                className="text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-lg outline-none focus-none bg-transparent w-full"
              />
            </div>
            {errors.primaryRate && (
              <p className="error">{errors.primaryRate}</p>
            )}
          </div>
          <div
            className="space-y-2"
            style={{ width: isMobile ? "100%" : "calc(33% - 12px)" }}
          >
            <div className="flex items-center">
              <p
                className="text-[14px] font-inter leading-[20px] font-medium"
                style={{ color: "#4A5353" }}
              >
                {language && language?.["KS Fiscal Code"]
                  ? language?.["KS Fiscal Code"]
                  : "KS Fiscal Code"}
              </p>
            </div>
            <div className="w-full">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                name="ksFiscalCode"
                placeholder={
                  language && language?.["Optional"]
                    ? language?.["Optional"]
                    : "Optional"
                }
                value={formValues.ksFiscalCode}
                onChange={handleChange}
                className="text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-lg outline-none focus-none bg-transparent w-full"
              />
            </div>
            {errors.ksFiscalCode && (
              <p className="error">{errors.ksFiscalCode}</p>
            )}
          </div>
          <div
            className="space-y-2"
            style={{ width: isMobile ? "100%" : "calc(33% - 12px)" }}
          >
            <div className="flex items-center">
              <p
                className="text-[14px] font-inter leading-[20px] font-medium"
                style={{ color: "#4A5353" }}
              >
                {language && language?.["Group TAX"]
                  ? language?.["Group TAX"]
                  : "Group TAX"}
              </p>
              <p
                className="text-[14px] font-inter leading-[20px]"
                style={{ color: "#BA0E13" }}
              >
                {language && language?.["*"] ? language?.["*"] : "*"}
              </p>
            </div>
            <div className="w-full">
              <DropdownSearch
                refKey="groupTax"
                refs={{ groupTax: dropdownGroupTaxRef }}
                openStates={{ groupTax: isGroupTaxDropdownOpen }}
                open={(key) =>
                  key === "groupTax" && setIsGroupTaxDropdownOpen(true)
                }
                close={(key) =>
                  key === "groupTax" && setIsGroupTaxDropdownOpen(false)
                }
                data={taxGroup}
                labelExtractor={(item) => item.groupName}
                value={searchGroupTax}
                setValue={setSearchGroupTax}
                onSelect={handleTaxGroupSelect}
                error={errors.taxGroupId}
                borderNone={false}
              />
            </div>
          </div>
          <div
            className="space-y-2"
            style={{ width: isMobile ? "100%" : "calc(33% - 12px)" }}
          >
            <div className="flex items-center">
              <p
                className="text-[14px] font-inter leading-[20px] font-medium"
                style={{ color: "#4A5353" }}
              >
                {language && language?.["Country"]
                  ? language?.["Country"]
                  : "Country"}
              </p>
              <p
                className="text-[14px] font-inter leading-[20px]"
                style={{ color: "#BA0E13" }}
              >
                {language && language?.["*"] ? language?.["*"] : "*"}
              </p>
            </div>
            <div className="w-full">
              <DropdownSearch
                refKey="type"
                refs={{ type: dropdownCountryRef }}
                openStates={{ type: isCountryDropdownOpen }}
                open={(key) => key === "type" && setIsCountryDropdownOpen(true)}
                close={(key) =>
                  key === "type" && setIsCountryDropdownOpen(false)
                }
                data={countries}
                labelExtractor={(item) => item.name}
                value={searchCountry}
                setValue={setSearchCountry}
                onSelect={handleCountrySelect}
                error={errors.countryId}
                borderNone={false}
              />
            </div>
          </div>
        </div>
      </form>
      <div
        className="w-full flex items-center justify-end"
        style={{ marginTop: "24px", gap: "12px" }}
      >
        <button
          type="button"
          onClick={() => {
            webNavigate("/" + preLanguage + "/tax");
          }}
          style={{
            padding: "10px 24px",
            borderRadius: "30px",
            color: "#222525",
            fontSize: "14px",
            fontWeight: "500",
            border: "1px solid #C9D7D8",
          }}
          className="font-inter cursor-pointer"
        >
          {language && language?.["Cancel"] ? language?.["Cancel"] : "Cancel"}
        </button>
        <button
          type="button"
          {...kb.primaryProps(handleSubmit)}
          style={{
            padding: "10px 24px",
            borderRadius: "30px",
            color: "#FFFFFF",
            fontSize: "14px",
            fontWeight: "500",
            backgroundColor: "#0F7679",
          }}
          className="font-inter cursor-pointer"
        >
          <p className="text-[14px] leading-[22px] font-inter">
            {language && language?.["Save"] ? language?.["Save"] : "Save"}
          </p>
        </button>
      </div>
      <PopupLocation
        show={showPopup}
        title={`${id === "new" ? (language && language?.["Create Tax"] ? language?.["Create Tax"] : "Create Tax") : language && language?.["Update Tax"] ? language?.["Update Tax"] : "Update Tax"}`}
        desc={`${id === "new" ? (language && language?.["Create Tax Failed"] ? language?.["Create Tax Failed"] : "Create Tax Failed") : language && language?.["Update Tax Failed"] ? language?.["Update Tax Failed"] : "Update Tax Failed"}`}
        onClose={handleClosePopup}
        onOpen={handleOpenPopup}
      />
    </div>
  );
};

export default CreateTax;
